'use client'

import Link from 'next/link'
import { createContext, useContext, useEffect, useState, type AnchorHTMLAttributes } from 'react'

/**
 * Where the marketing pages live in the URL.
 *
 * On meridiananesthesia.com the pages answer at clean paths (/about) because
 * middleware rewrites them to the internal /website/* routes. Everywhere else
 * — a Vercel preview, a local build, the platform host before its redirect —
 * they answer only at /website/*, and a clean link would 404. The base is read
 * from the address the visitor actually loaded, after mount, so the server
 * render (clean paths, the canonical case) never mismatches.
 */

const INTERNAL = '/website'
const BaseContext = createContext('')

export function SiteBaseProvider({ children }: { children: React.ReactNode }) {
  const [base, setBase] = useState('')
  useEffect(() => {
    const p = window.location.pathname
    if (p === INTERNAL || p.startsWith(`${INTERNAL}/`)) setBase(INTERNAL)
  }, [])
  return <BaseContext.Provider value={base}>{children}</BaseContext.Provider>
}

export function useSiteBase(): string {
  return useContext(BaseContext)
}

export function siteHref(base: string, path: string): string {
  if (!base || !path.startsWith('/')) return path
  return path === '/' ? base : `${base}${path}`
}

/** The visitor's current page as a clean path ("/about"), whichever host served it. */
export function cleanPath(pathname: string | null): string {
  if (!pathname) return '/'
  if (pathname === INTERNAL) return '/'
  if (pathname.startsWith(`${INTERNAL}/`)) return pathname.slice(INTERNAL.length)
  return pathname
}

type SiteLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { href: string }

/** An internal marketing link (a clean path from PATHS); external hrefs pass through untouched. */
export function SiteLink({ href, children, ...rest }: SiteLinkProps) {
  const base = useSiteBase()
  if (!href.startsWith('/')) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <Link href={siteHref(base, href)} {...rest}>
      {children}
    </Link>
  )
}
